import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';

type Variant = 'desktop' | 'mobile';

interface LanguageSwitcherProps {
  variant?: Variant;
}

const FLAGS: Record<SupportedLanguage, string> = {
  fr: '🇫🇷',
  en: '🇬🇧',
};

export default function LanguageSwitcher({ variant = 'desktop' }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const current = (SUPPORTED_LANGUAGES.includes(i18n.resolvedLanguage as SupportedLanguage)
    ? i18n.resolvedLanguage
    : 'fr') as SupportedLanguage;

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handleSelect = (lang: SupportedLanguage) => {
    void i18n.changeLanguage(lang);
    setOpen(false);
  };

  if (variant === 'mobile') {
    return (
      <div className="px-3 py-2">
        <p className="px-1 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-[#94A3B8]">
          {t('common.language')}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {SUPPORTED_LANGUAGES.map((lng) => {
            const isActive = lng === current;
            return (
              <button
                key={lng}
                type="button"
                onClick={() => handleSelect(lng)}
                className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-white/5 dark:text-[#94A3B8] dark:hover:bg-white/10'
                }`}
                aria-pressed={isActive}
              >
                <span aria-hidden="true">{FLAGS[lng]}</span>
                <span>{t(`language.${lng}`)}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-xl px-2.5 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:text-[#94A3B8] dark:hover:bg-white/5 dark:hover:text-white"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('common.switchLanguage')}
      >
        <span aria-hidden="true" className="text-base leading-none">{FLAGS[current]}</span>
        <span className="hidden sm:inline uppercase">{current}</span>
        <svg
          className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-40 rounded-2xl border border-slate-200/70 bg-white shadow-[0_4px_8px_rgba(15,23,42,0.06),0_18px_40px_-12px_rgba(15,23,42,0.20)] dark:border-white/10 dark:bg-card-dark overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150"
          role="listbox"
        >
          {SUPPORTED_LANGUAGES.map((lng) => {
            const isActive = lng === current;
            return (
              <button
                key={lng}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => handleSelect(lng)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300 font-semibold'
                    : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/5'
                }`}
              >
                <span aria-hidden="true" className="text-base leading-none">{FLAGS[lng]}</span>
                <span>{t(`language.${lng}`)}</span>
                {isActive && (
                  <svg className="ml-auto h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
